import { __decorate, __metadata, __rest } from 'tslib';
import { Service, StateManager, EventEmitter, SchematicUIService } from '@tencent/workbench';

const reservedFormItems = [
    { name: 'space', type: 'builtin' },
    { name: 'pusher', type: 'builtin' },
    { name: 'break', type: 'builtin' },
];
const reserved = reservedFormItems.map(item => item.name);
/**
 * @public
 */
let FormService = class FormService extends SchematicUIService {
    constructor(store, eventEmitter) {
        super();
        this.store = store;
        this.eventEmitter = eventEmitter;
        this.collapseCache = new Map();
        this.containers = [];
        this.getFormItem = (item, i, paths) => {
            if (Array.isArray(item)) {
                return item.map((v, j) => this.getFormItem(v, j, [...paths, `[${i}]`])).filter(Boolean);
            }
            const config = this.get(item);
            if (!config || config.name === 'none') {
                return null;
            }
            this.setPathToActionInfoInConfig(config, paths);
            return Object.assign(Object.assign({}, config), { id: item });
        };
        this.schema = {};
        this.store.register('form', []);
        this.store.register('activeForm', {});
        this.store.register('activeTab', {});
        reservedFormItems.forEach((config) => {
            this.configs.set(config.name, config);
        });
    }
    changeForm(id, tabIndex) {
        var _a, _b;
        const possibleCurrentContainer = this.containers[0];
        if (!possibleCurrentContainer) {
            this.pendingCurrentForm = { id, tabIndex };
            return;
        }
        const activeForm = (_a = this.store.getValue('activeForm')) !== null && _a !== void 0 ? _a : {};
        activeForm[possibleCurrentContainer] = id;
        this.store.update('activeForm', Object.assign({}, activeForm));
        if (typeof tabIndex === 'number') {
            const activeTab = (_b = this.store.getValue('activeTab')) !== null && _b !== void 0 ? _b : {};
            activeTab[possibleCurrentContainer] = `tab-${tabIndex}`;
            this.store.update('activeTab', Object.assign({}, activeTab));
        }
    }
    $forceUpdate() {
        var _a;
        const activeForm = (_a = this.store.getValue('activeForm')) !== null && _a !== void 0 ? _a : {};
        this.store.update('activeForm', Object.assign({}, activeForm));
    }
    onUpdate() {
        super.onUpdate();
        const root = [];
        Object.keys(this.schema).forEach((id) => {
            if (!this.configs.has(id)) {
                this.register({ name: id, type: 'builtin' });
            }
            const panel = this.getPanel(id);
            if (panel) {
                root.push(panel);
            }
        });
        this.store.update('form', root);
    }
    register(config, updateImmediately = true) {
        if (reserved.includes(config.name)) {
            return;
        }
        super.register(config, updateImmediately);
    }
    isPanel(item) {
        var _a;
        return ((_a = item.tabs) === null || _a === void 0 ? void 0 : _a.length) > 0;
    }
    getPanel(panelId) {
        const panelOrForm = this.schema[panelId];
        if (!panelOrForm) {
            return null;
        }
        if (this.isPanel(panelOrForm)) {
            const panel = panelOrForm;
            return {
                id: panelId,
                label: panel.label,
                tabs: panel.tabs.map((tab) => {
                    var _a;
                    return (Object.assign({ level: 'form', label: tab.label, sections: tab.sections.map(section => {
                            var _a;
                            return (Object.assign({ label: section.label, collapsable: section.collapsable, defaultCollapsed: section.defaultCollapsed, showSwitch: section.showSwitch, items: section.items.map((v, i) => { var _a; return this.getFormItem(v, i, [panelId, tab.label, (_a = section.key) !== null && _a !== void 0 ? _a : 'unnamed_section']); }).filter(Boolean) }, this.get((_a = section.key) !== null && _a !== void 0 ? _a : '')));
                        }).filter(s => s.items.length > 0) }, this.get((_a = tab.key) !== null && _a !== void 0 ? _a : '')));
                }),
            };
        }
        const form = panelOrForm;
        return {
            id: panelId,
            label: form.label,
            sections: form.sections.map(section => {
                var _a;
                return (Object.assign({ label: section.label, collapsable: section.collapsable, defaultCollapsed: section.defaultCollapsed, showSwitch: section.showSwitch, items: section.items.map((v, i) => { var _a; return this.getFormItem(v, i, [panelId, (_a = section.key) !== null && _a !== void 0 ? _a : 'unnamed_section']); }).filter(Boolean) }, this.get((_a = section.key) !== null && _a !== void 0 ? _a : '')));
            }).filter(s => s.items.length > 0),
        };
    }
    get(key) {
        return super.get(key, {
            name: 'none',
            type: 'custom',
        });
    }
    mergeSchemaWithoutUpdate(schema) {
        this.schema = Object.assign(Object.assign({}, this.schema), schema);
    }
    setCollapsed(key, collapsed) {
        this.collapseCache.set(key, collapsed);
        this.eventEmitter.emit('$formSectionCollapsed', { label: key, collapsed });
    }
    getCollapsed(key) {
        return this.collapseCache.get(key);
    }
    /**
     * @internal
     */
    newContainer(containerId) {
        this.containers.unshift(containerId);
        if (this.pendingCurrentForm) {
            const { id, tabIndex } = this.pendingCurrentForm;
            this.changeForm(id, tabIndex);
            this.pendingCurrentForm = undefined;
        }
    }
    /**
     * @internal
     */
    closeContainer(containerId) {
        var _a, _b;
        const idx = this.containers.indexOf(containerId);
        if (idx >= 0) {
            this.containers.splice(idx, 1);
            const activeTabs = (_a = this.store.getValue('activeTab')) !== null && _a !== void 0 ? _a : {};
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const _c = activeTabs, _d = containerId; _c[_d]; const newTabs = __rest(_c, [typeof _d === "symbol" ? _d : _d + ""]);
            this.store.update('activeTab', newTabs);
            const activeForms = (_b = this.store.getValue('activeForm')) !== null && _b !== void 0 ? _b : {};
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const _e = activeForms, _f = containerId; _e[_f]; const newForms = __rest(_e, [typeof _f === "symbol" ? _f : _f + ""]);
            this.store.update('activeForm', newForms);
        }
    }
};
FormService = __decorate([
    Service({ name: 'form', scope: 'ui:service' }),
    __metadata("design:paramtypes", [StateManager, EventEmitter])
], FormService);

export { FormService };
//# sourceMappingURL=form-service.js.map
